import React, { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { authService } from "../services/authService";
import { queryClient } from "../query/client";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => authService.getStoredUser()); // ✅ preload from localStorage
  const [loading, setLoading] = useState(() => authService.isAuthenticated());
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const initializeAuth = async () => {
      if (!authService.isAuthenticated()) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const freshUser = await Promise.race([
          authService.getMe(),
          new Promise((_, reject) =>
            setTimeout(() => reject(Object.assign(new Error("timeout"), { code: "TIMEOUT" })), 8000)
          ),
        ]);
        if (cancelled) return;
        setUser(freshUser);
        authService.storeUser(freshUser);
      } catch (error) {
        console.error("Auth initialization error:", error);
        if (error?.response?.status === 401) {
          authService.logout();
          queryClient.clear();
          if (!cancelled) setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initializeAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authService.login(credentials);
      const userPayload = response?.data?.user ?? response?.user;
      if (!userPayload) {
        throw new Error("Invalid login response: missing user");
      }
      queryClient.clear();
      authService.storeToken(response.token);
      authService.storeUser(userPayload);
      setUser(userPayload);
      toast.success("Signed in successfully");
      return response;
    } catch (error) {
      const msg = error.response?.data?.message || "Login failed";
      setError(msg);
      toast.error(msg);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authService.register(userData);
      const userPayload = response?.data?.user ?? response?.user;
      if (!userPayload) {
        throw new Error("Invalid registration response: missing user");
      }
      queryClient.clear();
      authService.storeToken(response.token);
      authService.storeUser(userPayload);
      setUser(userPayload);
      toast.success("Account created");
      return response;
    } catch (error) {
      const msg = error.response?.data?.message || "Registration failed";
      setError(msg);
      toast.error(msg);
      throw error;
    }
  };

  const registerAsAgent = async (agentData) => {
    try {
      setError(null);
      const response = await authService.registerAsAgent(agentData);
      const updatedUser = await authService.getMe();
      authService.storeUser(updatedUser); // ✅ fix
      setUser(updatedUser);
      return response;
    } catch (error) {
      const msg = error.response?.data?.message || "Agent registration failed";
      setError(msg);
      toast.error(msg);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    queryClient.clear();
    setUser(null);
    setError(null);
  };

  const updateUser = (updatedUserData) => {
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    authService.storeUser(updatedUser);
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    registerAsAgent,
    logout,
    updateUser,
    clearError,
    isAuthenticated: !!user,
    isAgent: user?.role === "agent",
    isAdmin: user?.role === "admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
